import React, { useEffect, useMemo, useRef } from "react";
import {
  View,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Text,
  useWindowDimensions,
  Button,
  Dimensions,
} from "react-native";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { COLORS, STRINGS } from "../../../constants";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import EventStore from "../../../storage/eventStore";
import AppSettings from "../../../storage/appSettings";
import Loading from "../../../components/Loading/Loading";
import NoResults from "../../../components/NoResults/NoResults";
import Calendar from "../../../components/FontAwesomeIcons/Calendar";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";
import ImageCarousel from "../../../components/ImageCarousel/ImageCarousel";
import { createHighlightedEvents } from "../../../utils/helper";
import {
  HighlightedEventsOverlay,
  onPressOverlay,
} from "../../../components/ImageCarouselOverlays/HighlightedEventsOverlay";
/**
 * ListViewCommon component displays a list of events.
 * It uses a FlatList to render each event and allows navigation to a details screen on press.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.navigation - The navigation object used to navigate between screens.
 * @param {Object} props.FontAwesomeIcon - The FontAwesomeIcon component to be used in the list.
 * @returns {JSX.Element} The ListViewCommon component that displays the list of events.
 */
const ListViewCommon = ({ route }) => {
  logAnalyticsEvent("screen_view", { name: "VBGo-EventsListView" });

  const { fontScale } = useWindowDimensions();
  const navigation = useNavigation();
  const initialLoading = EventStore((state) => state.initialLoading);
  const hasMore = EventStore((state) => state.hasMore);
  const filteredEvents = EventStore((state) => state.filteredEvents);
  const fetchMoreSearchResults = EventStore((state) => state.fetchMoreSearchResults);
  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const loading = EventStore((state) => state.loading);
  const applyFilters = EventStore((state) => state.applyFilters);
  const connectionError = AppSettings((state) => state.connectionError);
  const totalFound = EventStore((state) => state.totalFound);
  const featuredEvents = EventStore((state) => state.featuredEvents);
  const fetchCategories = EventStore((state) => state.fetchCategories);

  const ITEM_HEIGHT = 150 * fontScale;
  useMetaDescriptionOnFocus(STRINGS.metaListDescription);

  const { height } = Dimensions.get("window");

  const getItemLayout = (data, index) => ({
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
    index,
  });

  /**
   * Fetches events based on the selected "from" date.
   * If the selected date is earlier than today, it defaults to today's date.
   * Updates the application state with the adjusted date if necessary.
   * Extracts the day and month from the valid date and fetches events for that day.
   */
  const fetchEventsBySelectedFromDate = () => {
    let validDate = selectedFromDate ? new Date(selectedFromDate) : new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (validDate < today) {
      validDate = today;
      EventStore.setState({ selectedFromDate: today });
    }

    applyFilters();
  };
  /**
   * Reads category from route.params on mount and whenever it changes.
   * React Navigation populates route.params.category from the URL via the
   * linking config — so this handles direct links, refresh, and back navigation.
   */
  useEffect(() => {
    const categoryParam = route?.params?.category;

    const applyFromRouteParam = async () => {
      try {
        await fetchCategories();
        const allCategories = EventStore.getState().categories;

        // parse() in App.jsx always returns an array; guard for legacy string values too
        const raw = Array.isArray(categoryParam)
          ? categoryParam
          : categoryParam && categoryParam !== "undefined"
          ? String(categoryParam).split("|").map(decodeURIComponent).filter(Boolean)
          : [];

        const valid = raw.filter((cat) => allCategories.find((c) => c.name === cat));

        // Always update the store with the validated categories from route params.
        // This handles deep links, refresh, and back navigation from FiltersView.
        if (valid.length > 0) {
          EventStore.setState({ selectedCategories: valid });
        }
      } catch (error) {
        console.error("Error applying category from route param:", error);
      } finally {
        fetchEventsBySelectedFromDate();
      }
    };

    if (categoryParam !== undefined) {
      applyFromRouteParam();
    } else {
      fetchEventsBySelectedFromDate();
    }
  }, [route?.params?.category]);

  /**
   * Web only: When the screen regains focus (e.g., user presses browser back
   * button from FiltersView), read the current selectedCategories from the store,
   * update route params to sync the URL, and call applyFilters to refresh the list.
   * Skips the initial mount since the above useEffect already handles it.
   */
  const hasInitialFocusFired = useRef(false);
  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS === "web") {
        if (!hasInitialFocusFired.current) {
          hasInitialFocusFired.current = true;
          return;
        }
        const currentCategories = EventStore.getState().selectedCategories;
        navigation.setParams({
          category: currentCategories.length > 0 ? currentCategories : undefined,
        });
        applyFilters();
      }
    }, [applyFilters, navigation])
  );

  /**
   * Renders a single event item in the FlatList.
   */
  const renderItem = ({ item }) => {
    const formatted = item.occurrences?.[0]?.formatted || {};
    const title = item?.name;
    const subTitle = item?.location?.name || STRINGS.locationUnavailable;
    const categories = item.categoriesV2;

    return (
      <TouchableOpacity onPress={() => navigation.navigate("DetailsView", { eventKey: item?.occurrence_id })}>
        <View style={styles.cardContainer}>
          <View style={styles.leftSection}>
            <View style={styles.iconCircle}>
              <Calendar icon={faCalendar} size={Platform.OS === "web" ? "1x" : 16} color={COLORS.textLight} />
            </View>
          </View>
          <View style={styles.rightSection}>
            <Text style={styles.dayTimeText} testID="event-date">
              {formatted.startDate}
            </Text>
            <View style={styles.detailsContainer}>
              <Text style={[styles.titleText, { fontSize: 18 }]} numberOfLines={1} ellipsizeMode="tail">
                {title}
              </Text>
              <Text style={styles.subTitleText} numberOfLines={1} ellipsizeMode="tail">
                {subTitle}
              </Text>
              <Text style={styles.catText} numberOfLines={1} ellipsizeMode="tail">
                {(() => {
                  try {
                    return categories.join(", ").toUpperCase();
                  } catch (error) {
                    console.error("Error formatting categories = " + error);
                    return "";
                  }
                })()}
              </Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  /**
   * Header component for the FlatList, displaying a carousel of featured events.
   * Uses the ImageCarousel component to render the featured events with an overlay.
   */
  const headerComponent = useMemo(() => {
    const formattedFeaturedEvents = createHighlightedEvents(featuredEvents);

    return (
      <View style={{ backgroundColor: COLORS.textDarker }}>
        <ImageCarousel
          data={formattedFeaturedEvents}
          height={height * 0.3}
          overlay={HighlightedEventsOverlay(formattedFeaturedEvents)}
          onPress={onPressOverlay(navigation, formattedFeaturedEvents)}
        />
      </View>
    );
  }, [featuredEvents, height, navigation]);

  return (
    <>
      {initialLoading ? (
        <Loading />
      ) : connectionError || filteredEvents.length === 0 ? (
        <NoResults refresh={fetchEventsBySelectedFromDate} resultsCount={filteredEvents.length} />
      ) : (
        <FlatList
          data={filteredEvents}
          renderItem={renderItem}
          initialNumToRender={5}
          keyExtractor={(item) => item?.occurrence_id}
          getItemLayout={getItemLayout}
          ListHeaderComponent={headerComponent}
          ListFooterComponent={() =>
            hasMore ? (
              <View style={styles.footerContainer}>
                <Text style={styles.footerMoreText}>
                  Loaded {filteredEvents.length} out of {totalFound} events
                </Text>
                {loading ? (
                  <Loading />
                ) : (
                  <Button
                    title={STRINGS.moreEvents}
                    onPress={fetchMoreSearchResults}
                    color={COLORS.primary}
                    accessibilityLabel={STRINGS.moreEvents}
                  />
                )}
              </View>
            ) : null
          }
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    marginHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 10,
    paddingRight: 10,
    paddingVertical: 10,
    paddingTop: 8,
    backgroundColor: COLORS.textLight,
    borderRadius: 10,
    borderColor: COLORS.border,
    borderWidth: 0.5,
    marginVertical: 8,
  },
  leftSection: {
    marginRight: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.primary,
    justifyContent: "center",
    alignItems: "center",
    borderColor: COLORS.border,
    borderWidth: 1,
  },
  rightSection: {
    flex: 1,
  },
  dayTimeText: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.primary,
    marginTop: 6,
    lineHeight: 20,
  },
  detailsContainer: {
    marginTop: 8,
  },
  titleText: {
    fontWeight: "bold",
    color: COLORS.textDarker,
    lineHeight: 28,
  },
  subTitleText: {
    fontSize: 14,
    color: COLORS.border,
    marginTop: 4,
    lineHeight: 22,
  },
  catText: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.primary,
    marginTop: 4,
    lineHeight: 22,
  },
  footerContainer: {
    alignItems: "center",
    marginTop: 8,
    marginBottom: Platform.OS === "web" ? 8 : 40,
    borderWidth: 5,
    borderColor: COLORS.backgroundColor,
  },
  footerMoreText: {
    fontSize: 16,
    color: COLORS.textDark,
    marginTop: 6,
    lineHeight: 20,
    marginBottom: 8,
    width: "100%",
    textAlign: "center",
  },
});

export default React.memo(ListViewCommon);
